import AresBasicInfoDTO from "features/ares/dtos/AresBasicInfoDTO";
import IAresService from "features/ares/services/IAresService";
import PageCard from "features/shared/components/card/PageCard";
import FormGroup from "features/shared/forms/components/FormGroup";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React from "react";
import { getFormAddress } from "features/contracting/components/address/addressSubFormHelper";
import { BusinessDataSubForm_PageCard } from "features/shared/components/loaders/promiseLoadingAreas";
import { trackPromise } from "react-promise-tracker";
import { useTranslation } from "react-i18next";

type Props = {
  formHandler: UseFormHookResult;
  enableAresAutocomplete?: boolean;
  addressObjectPrefix?: string;
  disabled?: boolean;
};

const BusinessDataSubForm = ({
  formHandler,
  enableAresAutocomplete,
  addressObjectPrefix,
  disabled
}: Props) => {
  // DI
  const aresService = useInjection<IAresService>(IocTypes.AresService);

  //translations
  const tLabels = useTranslation("contracting\\contracting").t;

  // form hook props
  const { bindField, setFormValue, validateFormField, errors } = {
    ...formHandler,
  };

  // helpers
  const setAresInfoToFormValues = (aresInfo: AresBasicInfoDTO) => {
    setFormValue("businessName", aresInfo.businessCompany?.businessName);
    setFormValue("taxId", aresInfo.taxId);

    if (aresInfo.address && addressObjectPrefix) {
      const formAddress = getFormAddress(aresInfo.address);
      setFormValue(addressObjectPrefix, formAddress);
    }
  };

  // handlers
  const companyRegistrationNumberBlurHandler = async (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    if (enableAresAutocomplete) {
      const number = e.target.value;

      if (validateFormField("companyRegistrationNumber", number)) {
        const aresBasicInfo = await trackPromise(
          aresService.fetchAresBasicInfo(number),
          BusinessDataSubForm_PageCard
        );
        
        if (aresBasicInfo !== null) {
          setAresInfoToFormValues(aresBasicInfo);
        }
      }
    }
  };

  // render
  return (
    <PageCard
      title={tLabels("businessDataSubForm.cardTitle")}
      bodyClassName="pt-4 px-4"
      cardClassName="mb-4"
      loadingAreaName={BusinessDataSubForm_PageCard}
    >
      <div className="row no-gutters mx-n2">
        <div className="col-sm-6 px-2">
          <FormGroup
            labelText={tLabels("businessDataSubForm.companyRegistrationNumber.label")}
            inputId="companyRegistrationNumber"
            validationError={errors["companyRegistrationNumber"]}
          >
            <input
              id="companyRegistrationNumber"
              className="form-control"
              placeholder={tLabels("businessDataSubForm.companyRegistrationNumber.placeholder")}
              {...bindField("companyRegistrationNumber")}
              onBlur={companyRegistrationNumberBlurHandler}
              disabled={disabled}
            />
          </FormGroup>
        </div>
        <div className="col-sm-6 px-2">
          <FormGroup
            labelText={tLabels("businessDataSubForm.taxId.label")}
            inputId="taxId"
            validationError={errors["taxId"]}
          >
            <input
              id="taxId"
              className="form-control"
              placeholder={tLabels("businessDataSubForm.taxId.placeholder")}
              {...bindField("taxId")}
              disabled={disabled}
            />
          </FormGroup>
        </div>
        <div className="col-sm-6 px-2">
          <FormGroup
            labelText={tLabels("businessDataSubForm.businessName.label")}
            inputId="businessName"
            validationError={errors["businessName"]}
          >
            <input
              id="businessName"
              className="form-control"
              placeholder={tLabels("businessDataSubForm.businessName.placeholder")}
              {...bindField("businessName")}
              disabled={disabled}
            />
          </FormGroup>
        </div>
      </div>
    </PageCard>
  );
};

export default BusinessDataSubForm;
